import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  CollectDataPayload,
  CollectDataResponse
} from '../models/collect-data-payload';
import {
  TargetAudience,
  TargetAudienceUpdatePayload
} from '../models/target-audience';
import { TargetAudienceLocation } from '../models/target-audience-location';
import { TargetAreaSummary } from '../models/target-area-summary';
import { TargetCompanyResponse } from '../models/target-company';
import {
  CompanyContactResponse
} from '../models/company-contact';
import { CompanyServiceDetailsResponse } from '../models/company-service-details';
import { LinkedInProfileDetailsResponse } from '../models/linkedin-profile-details';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CollectDataService {
  private readonly http = inject(HttpClient);
  private readonly endpoint = `${environment.apiBaseUrl}/collect_data/`;

  submit(payload: CollectDataPayload): Observable<CollectDataResponse> {
    return this.http.post<CollectDataResponse>(this.endpoint, payload);
  }

  getCollectedDataHistory(): Observable<CollectDataResponse[]> {
    return this.http.get<CollectDataResponse[]>(this.endpoint);
  }

  fetchById(id: number): Observable<CollectDataResponse> {
    const url = `${environment.apiBaseUrl}/get_Collected_data/${id}`;
    return this.http.get<CollectDataResponse>(url);
  }

  generateTargetAudience(
    inputId: number
  ): Observable<{ success: boolean; message?: string }> {
    const url = `${environment.apiBaseUrl}/generate_target_audience`;
    return this.http.post<{ success: boolean; message?: string }>(url, {
      input_id: inputId
    });
  }

  analyzeData(
    inputId: number
  ): Observable<unknown> {
    const url = `${environment.apiBaseUrl}/analyze_data/${inputId}`;
    return this.http.post(url, {});
  }

  getTargetAudiences(serviceId: number): Observable<TargetAudience[]> {
    const url = `${environment.apiBaseUrl}/get_target_audience/${serviceId}`;
    return this.http.get<TargetAudience[]>(url);
  }

  updateTargetAudience(
    audienceId: number,
    payload: TargetAudienceUpdatePayload
  ): Observable<TargetAudience> {
    const url = `${environment.apiBaseUrl}/target_audience/${audienceId}`;
    return this.http.put<{ target_audience: TargetAudience }>(url, payload).pipe(
      // unwrap response payload
      map((response) => response.target_audience)
    );
  }

  deleteTargetAudience(
    audienceId: number
  ): Observable<{ success: boolean; message: string; deleted_id: number }> {
    const url = `${environment.apiBaseUrl}/target_audience/${audienceId}`;
    return this.http.delete<{ success: boolean; message: string; deleted_id: number }>(url);
  }

  createTargetAudienceLocations(
    serviceId: number,
    audienceIds?: number[]
  ): Observable<{ status: string; stored_rows: number; message?: string }> {
    const url = `${environment.apiBaseUrl}/create_target_audience_location`;
    let params = new HttpParams().set('service_collected_id', serviceId);
    if (audienceIds?.length) {
      audienceIds.forEach((id) => {
        params = params.append('audience_ids', String(id));
      });
    }
    return this.http.post<{ status: string; stored_rows: number; message?: string }>(
      url,
      null,
      {
        params
      }
    );
  }

  createTargetAudienceLocationsForAudience(
    serviceId: number,
    audienceId: number
  ): Observable<{ status: string; stored_rows: number; message?: string; audience_id?: number }> {
    const url = `${environment.apiBaseUrl}/create_target_audience_location/by_audience`;
    const params = new HttpParams()
      .set('service_collected_id', String(serviceId))
      .set('audience_id', String(audienceId));
    return this.http.post<{
      status: string;
      stored_rows: number;
      message?: string;
      audience_id?: number;
    }>(url, null, { params });
  }

  getLocationsByService(
    serviceId: number,
    audienceId?: number | null
  ): Observable<TargetAudienceLocation[]> {
    const url = `${environment.apiBaseUrl}/locations_by_service/${serviceId}`;
    return this.http.get<TargetAudienceLocation[]>(url);
  }

  getTargetAreaSummary(
    serviceId: number
  ): Observable<TargetAreaSummary[]> {
    const url = `${environment.apiBaseUrl}/target_area_summary/${serviceId}`;
    return this.http.get<TargetAreaSummary[]>(url);
  }

  generateTargetCompany(
    serviceId: number,
    locationId: number = 0
  ): Observable<{ status?: string; success?: boolean; message?: string }> {
    const url = `${environment.apiBaseUrl}/generate_target_company`;
    return this.http.post<{ status?: string; success?: boolean; message?: string }>(url, {
      service_collected_id: serviceId,
      location_id: locationId
    });
  }

  getTargetCompanies(
    serviceId: number,
    locationId?: number
  ): Observable<TargetCompanyResponse> {
    const url = `${environment.apiBaseUrl}/target_companies/`;
    let params = new HttpParams().set('service_collected_id', serviceId);
    if (locationId !== undefined) {
      params = params.set('location_id', locationId);
    }
    return this.http.get<TargetCompanyResponse>(url, { params });
  }

  getTargetCompanyAllLocations(
    serviceId: number
  ): Observable<Record<string, unknown>> {
    const url = `${environment.apiBaseUrl}/get_target_company_all_location`;
    const params = new HttpParams().set('service_collected_id', String(serviceId));
    return this.http.get<Record<string, unknown>>(url, { params });
  }

  generateCompanyContactDetails(
    serviceId: number,
    locationId: number,
    companyName?: string | null,
    companyWebsite?: string | null
  ): Observable<{ status?: string; success?: boolean; message?: string }> {
    const url = `${environment.apiBaseUrl}/company_contact_details`;
    let params = new HttpParams()
      .set('service_collected_id', serviceId)
      .set('location_id', locationId);
    if (companyName?.trim()) {
      params = params.set('company_name', companyName.trim());
    }
    if (companyWebsite?.trim()) {
      params = params.set('company_website', companyWebsite.trim());
    }
    return this.http.post<{ status?: string; success?: boolean; message?: string }>(
      url,
      null,
      { params }
    );
  }

  getCompanyContactDetails(
    serviceId: number,
    locationId: number
  ): Observable<CompanyContactResponse> {
    const url = `${environment.apiBaseUrl}/get_company_contact_details`;
    const params = new HttpParams()
      .set('service_collected_id', serviceId)
      .set('location_id', locationId);
    return this.http.get<CompanyContactResponse>(url, { params });
  }

  getCompanyServiceDetails(
    serviceId: number,
    locationId: number,
    companyName?: string | null,
    companyWebsite?: string | null
  ): Observable<CompanyServiceDetailsResponse> {
    const url = `${environment.apiBaseUrl}/company_service_details`;
    let params = new HttpParams()
      .set('service_collected_id', serviceId)
      .set('location_id', locationId);
    if (companyName?.trim()) {
      params = params.set('company_name', companyName.trim());
    }
    if (companyWebsite?.trim()) {
      params = params.set('company_website', companyWebsite.trim());
    }
    return this.http.get<CompanyServiceDetailsResponse>(url, { params });
  }

  refreshLinkedInProfileDetails(
    serviceId: number,
    contactId: number,
    locationId?: number | null,
    searchQuery?: string | null
  ): Observable<LinkedInProfileDetailsResponse> {
    const url = `${environment.apiBaseUrl}/linkedin_profile_details/refresh`;
    const body: Record<string, unknown> = {
      service_id: serviceId,
      contact_id: contactId
    };
    if (locationId != null) {
      body['location_id'] = locationId;
    }
    if (searchQuery?.trim()) {
      body['search_query'] = searchQuery.trim();
    }
    return this.http.post<LinkedInProfileDetailsResponse>(url, body);
  }

  getLinkedInProfileDetails(
    serviceId: number,
    contactId: number,
    locationId?: number | null
  ): Observable<LinkedInProfileDetailsResponse> {
    const url = `${environment.apiBaseUrl}/linkedin_profile_details`;
    let params = new HttpParams()
      .set('service_id', String(serviceId))
      .set('contact_id', String(contactId));
    if (locationId != null) {
      params = params.set('location_id', String(locationId));
    }
    return this.http.get<LinkedInProfileDetailsResponse>(url, { params });
  }

  exportCompanyContactDetails(
    serviceId: number,
    locationId: number
  ): Observable<Blob> {
    const url = `${environment.apiBaseUrl}/get_company_contact_details/export`;
    const params = new HttpParams()
      .set('service_collected_id', serviceId)
      .set('location_id', locationId);
    return this.http.get<Blob>(url, { params, responseType: 'blob' as 'json' });
  }
}

